import React, { useState, useMemo, useContext } from 'react';
import Tile from './Tile';
import { calculateGroupOutline, calculateBoundingBox, BACKGROUND_DARKENING } from '../utils/tileGroupUtils';
import { SvgZoomContext } from '../contexts/SvgZoomContext';
import { useStitchedTiles } from '../utils/tileStitcher';
import './SvgComponents.css';
import {registry} from '../registryData';

var SCALE = 2

interface ComponentConfig {
    component: React.ComponentType<any>;
    props: {
        cx?: number;
        cy?: number;
        r?: number;
        tx?: number;
        ty?: number;
        texture?: string;
    };
    key: string;
}

interface TileGroupProps {
    specialTiles: ComponentConfig[];
    facadeTiles: [number, number][];
    groupCoordinates: [number, number][];
    groupName: string;
    displayText: string;
    backgroundTexture: string;
    foreignObjectComponent?: React.ComponentType<any>;
    foreignObjectProps?: any;
    zoomScale?: number;
}

const TileGroup: React.FC<TileGroupProps> = ({ 
    specialTiles, 
    facadeTiles, 
    groupCoordinates, 
    groupName, 
    displayText, 
    backgroundTexture,
    foreignObjectComponent: ForeignObjectComponent,
    foreignObjectProps = {},
    zoomScale = SCALE
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const context = useContext(SvgZoomContext);

    // Sync isSelected with global state
    React.useEffect(() => {
        if (context?.selectedTileGroup === groupName){
            setIsSelected(true);
            setIsHovered(false); // Disable hover when selected
        } else {
            setIsSelected(false);
        }
    }, [context?.selectedTileGroup, groupName]);

    // Generate unique filter ID
    const filterId = useMemo(() => `glow-${Math.random().toString(36).substr(2, 9)}`, []);

    // Calculate the outline path and bounding box
    const outlinePath = useMemo(() => calculateGroupOutline(groupCoordinates), [groupCoordinates]);
    const boundingBox = useMemo(() => calculateBoundingBox(groupCoordinates), [groupCoordinates]);

    /*
    CALCULATE GROUP CENTER AND BOUNDS
    */
    const groupCenter = useMemo(() => {
        if (groupCoordinates.length === 0) return { x: 0, y: 0, topY: 0 };
        const xs = groupCoordinates.map(([x]) => x);
        const ys = groupCoordinates.map(([, y]) => y);
        return {
            x: (Math.min(...xs) + Math.max(...xs)) / 2 + 16, // +16 for tile center
            y: (Math.min(...ys) + Math.max(...ys)) / 2 + 16,
            topY: Math.min(...ys) // Top of the group for text positioning
        };
    }, [groupCoordinates]);
    const foreignObjectBounds = useMemo(() => {
        if (groupCoordinates.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
        const xs = groupCoordinates.map(([x]) => x);
        const ys = groupCoordinates.map(([, y]) => y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return {
            x: minX,
            y: minY,
            width: maxX - minX + 32, // +32 for tile width
            height: maxY - minY + 32 // +32 for tile height
        };
    }, [groupCoordinates]);

    const handleClick = () => {
        // Prevent state changes during animation
        if (context?.isAnimating) return;
        
        const newSelectedGroup = context?.selectedTileGroup === groupName ? null : groupName;
        context?.setSelectedTileGroup(newSelectedGroup);
    };

    // Create stitched background tiles for better performance
    const { stitchedTile: backgroundStitchedTile, isLoading: backgroundLoading, error: backgroundError } = useStitchedTiles(
        groupCoordinates,
        backgroundTexture,
        32
    );

    // Set CSS custom property for darkening
    React.useEffect(() => {
        //The tilegroup components upon useeffect should note down the location of xtranslate ytranslate and scale in the registry for the groupName
        if (registry[groupName]) {
            registry[groupName].xTranslate = groupCenter.x;
            registry[groupName].yTranslate = groupCenter.y;
            registry[groupName].scale = zoomScale;
        }
        document.documentElement.style.setProperty('--darkening-amount', BACKGROUND_DARKENING.toString());
    }, []);

    return (
        <>
            {/* Background tiles (darkened) - stitched for performance */}
            {backgroundStitchedTile && !backgroundLoading && (
                <image
                    x={backgroundStitchedTile.bounds.minX}
                    y={backgroundStitchedTile.bounds.minY}
                    width={backgroundStitchedTile.bounds.width}
                    height={backgroundStitchedTile.bounds.height}
                    href={backgroundStitchedTile.dataUrl}
                    className="tile-group-darkened"
                    aria-label={`Background tiles for ${groupName} group`}
                />
            )}
            {/* Fallback to individual tiles if stitching fails */}
            {(backgroundError || backgroundLoading) && groupCoordinates.map(([x, y], index) => (
                <Tile 
                    key={`${groupName}-bg-fallback-${x}-${y}-${index}`}
                    tx={x} 
                    ty={y} 
                    texture={backgroundTexture}
                    className="tile-group-darkened"
                />
            ))}
            
            {/* Black effect */}
            {outlinePath && (
                <>
                    {!isSelected && (<defs>
                        <filter id={filterId}>
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>)}
                    <path
                        d={outlinePath}
                        stroke="black"
                        strokeWidth="2"
                        fill="none"
                        filter={`url(#${filterId})`}
                        className="tile-group-glow"
                    />
                </>
            )}
            
            {/* Render tiles */}
            {specialTiles
                .filter(({ props }) => {
                    if (!isSelected) return true;
                    // When selected, exclude tiles that are in facadeTiles
                    return !facadeTiles.some(([fx, fy]) => props.tx === fx && props.ty === fy);
                })
                .map(({ props, key }) => (
                    <Tile 
                        key={key} 
                        tx={props.tx!} 
                        ty={props.ty!} 
                        texture={props.texture!} 
                    />
                ))}

            {/* White effect */}
            {(isHovered || isSelected) && outlinePath && (
                <>
                    {!isSelected && (<defs>
                        <filter id={filterId}>
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge> 
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>)}
                    <path
                        d={outlinePath}
                        stroke="white"
                        strokeWidth="2"
                        fill="none"
                        filter={`url(#${filterId})`}
                        className="tile-group-glow-white"
                    />
                </>
            )}
            
            {/* Foreign object overlay */}
            {groupCoordinates.length > 0 && ForeignObjectComponent && (
                <foreignObject
                    x={foreignObjectBounds.x}
                    y={foreignObjectBounds.y}
                    width={foreignObjectBounds.width}
                    height={foreignObjectBounds.height}
                >
                    <ForeignObjectComponent
                        {...foreignObjectProps}
                        bounds={foreignObjectBounds}
                        groupName={groupName}
                        displayText={displayText}
                        isHovered={isHovered}
                        isSelected={isSelected}
                    />
                </foreignObject>
            )}
            
            {/* Hover area - covers entire group region (rendered last to be on top) */}
            {!isSelected && groupCoordinates.map(([x, y], index) => (
                <rect
                    key={`hover-${x}-${y}-${index}`}
                    x={x}
                    y={y}
                    width={32}
                    height={32}
                    fill="transparent"
                    className="tile-group-hover-area"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={handleClick}
                />
            ))}
        </>
    );
};

export default TileGroup;